import { faCircle } from "@fortawesome/pro-regular-svg-icons/faCircle";
import { Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, Switch, TextField, Typography } from '@mui/material';
import { RequestLifeCycle } from 'app/appModels';
import { MprcButton, MprcContainer, MprcIconFa, MprcRequestErrorDialog, MprcRequestErrorReport, MprcRequestIndicator } from 'components';
import { ButtonAdd } from 'components/buttons';
import { IconButtonClose, IconButtonDelete } from 'components/iconButtons';
import { AutoCompleteHierarchicalModel } from "components/MprcAutoCompleteHierarchical";
import { MprcDatePicker } from "components/MprcDatePicker";
import { MprcTimePicker } from "components/MprcTimePicker";
import { CreateOrUpdatePollCommand, GetPollPublicationDto, PollItemModel, PublicationApprovalStatus, PublicationCreationSettingsResponse, PublicationTagItem, PublicationType, TopicAutoCompleteHierarchicalModelDto } from 'generated/CoreApi_v1';
import { useReferredState } from "hooks/useReferredState";
import LibUtils from 'lib/LibUtils';
import moment, { Moment } from 'moment';
import { useSnackbar } from "notistack";
import { useCoreApi } from 'providers/CoreApiProvider';
import { useSession } from 'providers/SessionProvider';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from "react-router";
import { KnowledgePaths } from "routes/KnowledgeRouter";
import { NIL, v4 as uuidv4 } from 'uuid';
import { IStickerBoxValidation } from "views/foundation/sticker/components/StickerBox";
import { publish } from "views/knowledge/feed/FeedEvents";
import { ICreatePublicationProps } from "views/knowledge/publication/creation";
import { IInstitutionalBoxValidation, IPublicationChannelBoxValidation, PublicationAdvancedBox, PublicationChannelBox, PublicationTopicsBox } from './components';

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IValidation {
    title?: boolean;
    answers?: boolean;
    dateUntil?: boolean;
    timeUntil?: boolean;
}

interface IState {
    record: GetPollPublicationDto;
    validation: IValidation & IPublicationChannelBoxValidation & IInstitutionalBoxValidation & IStickerBoxValidation;
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const CreatePoll = (props: ICreatePublicationProps) => {

    const { onClose } = props;

    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();
    const api = useCoreApi();
    const session = useSession();

    const refTitle = useRef<HTMLTextAreaElement | undefined>(undefined);
    let timerLastChange: number;
    let timer: ReturnType<typeof setTimeout>;
    const defaultAnswer = (id: string) => ({ id } as PollItemModel);
    const minAnswers = 2;
    const maxAnswers = 7;

    const [step, setStep] = useState<number>(1);
    const [formCycle, setFormCycle] = useState<RequestLifeCycle>(RequestLifeCycle.None);
    const [lastError, setLastError] = useState<any>();
    const [dateUntil, setDateUntil] = useState<moment.Moment | null>(null);
    const [timeUntil, setTimeUntil] = useState<moment.Moment | null>(null);
    const [loadDto, setLoadDto] = useState<GetPollPublicationDto>();
    const [recordChanged, setRecordChanged] = useState<boolean>(false);

    const config = useRef<PublicationCreationSettingsResponse | null>(null);

    const [state, setState, refState] = useReferredState<IState>(
        {
            record: {
                publicationId: NIL,
                content: "",
                title: "",
                topics: [],
                attachs: [],
                anonymousAnswers: true,
                channel: null!,
                isPrivate: false,
                publicationType: PublicationType.Poll,
                answers: [defaultAnswer(uuidv4()), defaultAnswer(uuidv4())],
                allowAnswersUntil: null,
                hasAnswers: false,
                presentation: null,
                advancedOptions: {
                    isInstitutional: false,
                    viewPeriodStart: null,
                    viewPeriodEnd: null,
                    viewStickerUntil: null,
                    sticker: null,
                    tags: []
                }
            },
            validation: {
                title: undefined,
                answers: undefined,
                channel: undefined,
                dateUntil: undefined,
                timeUntil: undefined,
                period: true,
                viewStickerUntil: true
            }
        });

    const autoFocusIndex = state.record.title == "" ? -1 : state.record.answers.findIndex(p => (p.text?.length ?? 0) == 0);
    const readOnly = state.record.hasAnswers;
    const endOfDay = state.record.allowAnswersUntil != null
        && state.record.allowAnswersUntil.getHours() == 23
        && state.record.allowAnswersUntil.getMinutes() == 59
        && state.record.allowAnswersUntil.getSeconds() == 59;

    const steps = [];
    const StepFirstPage = 1;
    const StepSecondPage = 2;

    ///
    const updateRecord = (data: Partial<GetPollPublicationDto>) => {
        return { ...refState.current.record, ...data };
    };

    ///
    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const value = event.target.value;
        setState({
            ...state,
            record: updateRecord({ title: value }),
            validation: { ...state.validation, title: value.length > 0 }
        });
    };

    ///
    const handleAnswerChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>, index: number) => {
        const value = event.target.value;

        const newAnswers = state.record.answers.map((v, i) => {
            if (i == index) {
                v.text = value;
            }
            return v;
        });

        setState({
            ...state,
            record: updateRecord({ answers: newAnswers }),
            validation: { ...state.validation, answers: state.record.answers.every(x => x.text?.length > 0) }
        });
    };

    //
    const handleValidUntil = (dateUntil: Moment | null, timeUntil: Moment | null) => {
        let allowAnswersUntil = null;
        if (dateUntil != null) {
            allowAnswersUntil = dateUntil.toDate();
            allowAnswersUntil.setHours(timeUntil == null ? 23 : timeUntil.hours());
            allowAnswersUntil.setMinutes(timeUntil == null ? 59 : timeUntil.minutes());
            allowAnswersUntil.setSeconds(timeUntil == null ? 59 : timeUntil.seconds());
        }

        setState({
            ...state,
            record: updateRecord({ allowAnswersUntil: allowAnswersUntil ?? null }),
        });
    };

    ///
    const handleTitleKeyUp = (event: React.KeyboardEvent<HTMLDivElement>) => {

        if (timer) clearTimeout(timer);

        if (!refTitle.current) return;
        timerLastChange = new Date().getTime();

        timer = setTimeout(() => {
            if ((new Date().getTime() - timerLastChange) > 200) {
                //  Adiciona "?" no final do texto.
                if (refTitle.current!.value.length > 0 && refTitle.current!.value[refTitle.current!.value.length - 1] != "?") {
                    refTitle.current!.value += "?";
                    setState({
                        ...state,
                        record: updateRecord({ title: refTitle.current!.value }),
                    });
                    handleTitleCaret();
                }
            }
        }, 200);
    };

    ///
    const handleTitleCaret = () => {
        // Reposiciona CARET antes do "?" do final do texto.
        if (refTitle.current) {
            if (refTitle.current.selectionStart == refTitle.current.selectionEnd) { // Garante que não está selecionando um texto
                if (refTitle.current.value.length > 1 && (refTitle.current.selectionEnd ?? 0) > refTitle.current.value.length - 1) {
                    refTitle.current.selectionStart = refTitle.current.selectionEnd = refTitle.current.value.length - 1;
                }
            }
        }
    };

    ///
    const handleAddOption = () => {

        if (state.record.answers.length < maxAnswers) {
            const currentAnswers = state.record.answers ?? [];
            const newAnswers = [...currentAnswers, defaultAnswer(uuidv4())];

            setState({
                ...state,
                record: updateRecord({ answers: newAnswers }),
                validation: { ...state.validation, answers: newAnswers.every(x => x.text?.length > 0) }
            });
        }
    };

    ///
    const handleDelete = (index: number) => {

        if (state.record.answers.length > minAnswers) {
            const newAnswers = state.record.answers;
            newAnswers.splice(index, 1);

            setState({
                ...state,
                record: updateRecord({ answers: newAnswers }),
                validation: { ...state.validation, answers: newAnswers.every(x => x.text?.length > 0) }
            });
        }
    };

    ///
    const canGoStep2 = (): boolean => {
        const { validation } = state;

        return validation.title === true
            && validation.answers === true
            && (validation.dateUntil === true || validation.dateUntil == undefined)
            && (validation.timeUntil === true || validation.timeUntil == undefined);
    };

    ///
    const canSave = (): boolean => {
        const { validation } = state;
        return canGoStep2() && validation.channel === true && validation.period === true && (validation.viewStickerUntil == undefined || validation.viewStickerUntil == true) && recordChanged;

    };

    ///
    const save = () => {
        const { record } = state;

        const dto: CreateOrUpdatePollCommand = {
            publicationId: record.publicationId,
            title: record.title!,
            content: record.content,
            anonymousAnswers: record.anonymousAnswers,
            allowAnswersUntil: record.allowAnswersUntil,
            channelId: record.channel.channelId,
            isPrivate: record.isPrivate,
            answers: record.answers,
            topics: record.topics.map(t => t.key),
            contentUnformatted: null,
            threadId: null,
            advancedOptions: {
                isInstitutional: record.advancedOptions.isInstitutional,
                stickerId: record.advancedOptions.sticker?.stickerId ?? null,
                viewStickerUntil: record.advancedOptions.viewStickerUntil,
                viewPeriodEnd: record.advancedOptions.viewPeriodEnd,
                viewPeriodStart: record.advancedOptions.viewPeriodStart,
                tags: record.advancedOptions.tags.map((tag) => ({ tagId: tag.tagId, tagName: tag.tagName } as PublicationTagItem))
            },
        };

        setFormCycle(RequestLifeCycle.Posting);

        api.v1.knowledgePublication.createOrUpdatePollPublication(dto)
            .then((response) => {
                onClose(response.data);
                setFormCycle(RequestLifeCycle.None);

                if (response.data.newPublicationId) {
                    if (response.data.approvalStatus == PublicationApprovalStatus.Approved) {
                        publish("PublicationCreated", response.data.newPublicationId);
                        publish("AuthorNewPublication", session.user.userId);
                        publish("ChannelNewPublication", dto.channelId);
                        dto.topics.forEach(topicId => publish("TopicNewPublication", topicId));

                        enqueueSnackbar(
                            t("Publication.CreatePoll.PublishMessage")
                            , {
                                variant: "mprcAction",
                                action: <Link to={KnowledgePaths.PublicationViewerResolver(response.data.newPublicationId, PublicationType.Poll)}>{t("Publication.Messages.OpenPublication")}</Link>
                            }
                        );
                    }
                    else {
                        enqueueSnackbar(t("Publication.CreatePoll.PublishModerationMessage"), { variant: "warning" });
                    }
                }
                else {
                    if (response.data.approvalStatus == PublicationApprovalStatus.Approved) {
                        publish("PublicationUpdate", record.publicationId);
                        enqueueSnackbar(t("Publication.CreatePoll.PublishUpdateMessage"), { variant: "success" });
                    }
                    else {
                        enqueueSnackbar(t("Publication.CreatePoll.PublishModerationUpdateMessage"), { variant: "warning" });
                    }
                }
            })
            .catch((error) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.PostFailed);
            });
    };

    ///
    const setDefaultValues = () => {
        if (config.current && config.current.defaultChannel.length > 0) {
            const item = config.current.defaultChannel[0];
            setState({
                ...state,
                record: updateRecord({ channel: { channelId: item.key, name: item.label, isPrivate: item.isPrivate, requirePublicationEditJustification: false, thumbnailUrl: item.thumbnailUrl! } }),
                validation: { ...state.validation, channel: true }
            });
        }
        if (config.current && config.current.topics.length > 0) {
            setState({
                ...state,
                record: updateRecord({ topics: config.current.topics }),
            });
        }
    };

    ///
    const loadSettings = async () => {
        setFormCycle(RequestLifeCycle.Loading);

        api.v1.knowledgePublication.getCreationSettings({ defaultChannelId: props.channelId ?? null, defaultTopicId: props.topicId ?? null, exclusiveListId: null })
            .then((result) => {
                config.current = result.data;
                setDefaultValues();
                setFormCycle(RequestLifeCycle.None);
            })
            .catch((error: any) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.LoadFailed);
            });
    };

    ///
    const load = async (publicationId: string) => {
        setFormCycle(RequestLifeCycle.Loading);

        Promise.all([
            api.v1.knowledgePublication.getCreationSettings({ defaultChannelId: null, defaultTopicId: null, exclusiveListId: null }),
            api.v1.knowledgePublication.getPollPublication({ publicationId }),
        ])
            .then((results) => {
                config.current = results[0].data;

                const record = results[1].data;

                setState({
                    record,
                    validation: {
                        title: true,
                        answers: true,
                        channel: true,
                        dateUntil: true,
                        timeUntil: true,
                        period: true,
                        viewStickerUntil: true
                    }
                });
                setLoadDto(LibUtils.deepCopy(record));
                setDateUntil(record.allowAnswersUntil == null ? null : moment(record.allowAnswersUntil));
                setTimeUntil(record.allowAnswersUntil == null ? null : moment(record.allowAnswersUntil));
                setFormCycle(RequestLifeCycle.None);
            })
            .catch((error: any) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.LoadFailed);
            });
    };

    ///
    steps[StepFirstPage] = () => {
        const { record, validation } = state;

        return (
            <>
                <DialogContent sx={{ display: "flex", flexDirection: "column" }}>

                    <MprcContainer>
                        <TextField
                            autoFocus
                            fullWidth
                            name="title"
                            margin="normal"
                            multiline={true}
                            placeholder={t("Publication.CreatePoll.TitlePlaceholder")}
                            autoComplete="off"
                            tabIndex={0}
                            slotProps={{
                                input: {
                                    inputProps: { maxLength: 255 },
                                    style: { fontSize: 18 },
                                    readOnly
                                }
                            }}
                            value={record.title}
                            inputRef={refTitle}
                            onChange={e => handleTitleChange(e)}
                            onKeyUp={handleTitleKeyUp}
                            error={validation.title === false}
                        />
                    </MprcContainer>

                    <MprcContainer>
                        <Grid container spacing={1}>
                            <Grid size="grow">
                                {record.answers.map((v, index) =>
                                    <Grid key={index} container alignContent="center" alignItems="center" spacing={1}>
                                        <Grid>
                                            <MprcIconFa icon={faCircle} />
                                        </Grid>
                                        <Grid size="grow">
                                            <TextField
                                                id={record.answers[index].id}
                                                fullWidth
                                                autoFocus={index == autoFocusIndex}
                                                autoComplete="off"
                                                placeholder={t("Publication.CreatePoll.OptionPlaceholder")}
                                                slotProps={{
                                                    input: {
                                                        inputProps: { maxLength: 255 }
                                                    }
                                                }}
                                                error={record.answers[index].text != undefined && validation.answers === false && record.answers[index].text?.length == 0}
                                                value={record.answers[index].text || ''}
                                                disabled={readOnly}
                                                onChange={e => handleAnswerChange(e, index)}
                                                onKeyDown={
                                                    (e) => {
                                                        if (e.keyCode == 13) {
                                                            if (index < record.answers.length - 1) {
                                                                const el = document.getElementById(record.answers[index + 1].id);
                                                                if (el) el.focus();
                                                            }
                                                            else if (record.answers[index].text?.length > 0 && record.answers.length < maxAnswers) {
                                                                handleAddOption();
                                                            }
                                                        }
                                                    }
                                                }
                                            />
                                        </Grid>
                                        {
                                            record.answers.length > minAnswers && !readOnly &&
                                            <Grid>
                                                <IconButtonDelete
                                                    size="small"
                                                    onClick={() => handleDelete(index)}
                                                />
                                            </Grid>
                                        }
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </MprcContainer>
                    <MprcContainer>
                        {record.answers.length < maxAnswers && !readOnly &&
                            <ButtonAdd
                                sx={{ pl: 0 }} text={t("Publication.CreatePoll.AddOptionButtonText")}
                                onClick={handleAddOption}
                            />
                        }
                    </MprcContainer>
                    <MprcContainer>
                        <Grid container justifyContent={"space-between"} alignItems="center" direction="row">
                            <Grid size="grow">
                                <Grid container spacing={2}>
                                    <Grid>
                                        <MprcDatePicker
                                            width={200}
                                            error={validation.dateUntil === false}
                                            label={t("Publication.CreatePoll.DateAllowAnswersUntilLabel")}
                                            value={dateUntil?.toDate() ?? null}
                                            onError={(reason, v) => setState({ ...state, validation: { ...validation, dateUntil: reason == undefined } })}
                                            onChange={(date, v) => { const dt = moment(date); setDateUntil(dt); handleValidUntil(dt, timeUntil); }}
                                            minDate={moment().add(1, 'days').toDate()}
                                        />
                                    </Grid>
                                    {dateUntil &&
                                        <Grid size={{ xs: 3 }}>
                                            <MprcTimePicker
                                                error={validation.timeUntil === false}
                                                label={t("Publication.CreatePoll.TimeAllowAnswersUntilLabel")}
                                                value={endOfDay ? null : timeUntil?.toDate() ?? null}
                                                onError={(reason, v) => setState({ ...state, validation: { ...validation, timeUntil: reason == undefined } })}
                                                onChange={(time, v) => { const t = moment(time); setTimeUntil(t); handleValidUntil(dateUntil, t); }}
                                            />
                                        </Grid>
                                    }
                                </Grid>
                                <Grid>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={record.anonymousAnswers}
                                                onChange={() => setState({ ...state, record: updateRecord({ anonymousAnswers: !record.anonymousAnswers }) })}
                                            />}
                                        label={t("Publication.CreatePoll.IsAnonymousLabel")}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </MprcContainer>
                </DialogContent>
                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="center" spacing={1} width="100%">
                        <Grid>
                            <b>{t("Publication.CreatePoll.Footer1")}</b>
                            <span>&nbsp;&raquo;&nbsp;</span>
                            <span>{t("Publication.CreatePoll.Footer2")}</span>
                        </Grid>
                        <Grid>
                            <MprcButton
                                variant="contained"
                                color="primary"
                                disabled={!canGoStep2()}
                                onClick={() => setStep(2)}>
                                <span>{t("Publication.Common.Form.ButtonContinueText")}</span>
                            </MprcButton>
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };

    ///
    steps[StepSecondPage] = () => {
        const { record, validation } = state;
        return (
            <>
                <DialogContent>

                    <PublicationChannelBox
                        channel={record.channel}
                        suggestions={config.current!.channelSuggestions}
                        onUpdateState={({ channel, validation }) => setState({
                            ...state,
                            record: updateRecord({ channel: channel! }),
                            validation: { ...state.validation, ...validation }
                        })}
                        validation={validation}
                    />

                    <PublicationTopicsBox
                        channelId={record.channel?.channelId}
                        topics={record.topics as AutoCompleteHierarchicalModel[]}
                        suggestions={[]}
                        publication={{ title: record.title, content: record.content }}
                        onUpdateState={
                            (topics) => setState({
                                ...state,
                                record: updateRecord({ topics: topics as TopicAutoCompleteHierarchicalModelDto[] })
                            })
                        }
                    />

                    <PublicationAdvancedBox
                        channelId={record.channel?.channelId}
                        allowInstitutionalPublisher={config.current?.allowInstitutionalPublisher ?? false}
                        canCreateTags={config.current?.canCreateTags ?? false}
                        onUpdateState={({ advancedOptions, validation }) => {
                            setState({
                                ...state,
                                record: updateRecord({ advancedOptions: { ...advancedOptions } }),
                                validation: { ...state.validation, ...validation }
                            });
                        }}
                        advancedOptions={{ ...record.advancedOptions }}
                        validation={validation}
                    />
                </DialogContent>

                <DialogActions>
                    <Grid container justifyContent="end" alignItems="center" spacing={2} width="100%">
                        <Grid>
                            <MprcButton
                                variant="contained"
                                onClick={() => setStep(1)}
                                sx={{ mr: 1 }}
                            >
                                <span>{t("Publication.Common.Form.ButtonBackText")}</span>
                            </MprcButton>

                            <MprcButton
                                variant="contained"
                                color="success"
                                onClick={() => save()}
                                disabled={!canSave()}
                            >
                                <span>{t("Publication.Common.Form.ButtonFinishText")}</span>
                            </MprcButton>
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };

    ///////////////////////////////////////////////////////////////////////
    /// Effect's
    ///////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (props.publicationId)
            load(props.publicationId);
        else
            loadSettings();
    }, []);

    useEffect(() => {
        const isRecordChanged = !LibUtils.deepCompare(state.record, loadDto);
        setRecordChanged(isRecordChanged);
    }, [state]);

    ///////////////////////////////////////////////////////////////////////
    /// RENDER
    ///////////////////////////////////////////////////////////////////////
    return (
        <Dialog
            open={true}
            maxWidth={"md"}
            fullWidth
        >
            <DialogTitle>
                <Grid container justifyContent="space-between">
                    <Grid>
                        <Typography variant="h4">
                            {t(state.record.publicationId == NIL ? "Publication.CreatePoll.TitleNew" : "Publication.CreatePoll.TitleEdit")}
                        </Typography>
                        {state.record.hasAnswers && <Typography variant="bodyNotice">{t("Publication.CreatePoll.PeopleAlreadyVotedMessage")}</Typography>}
                    </Grid>
                    <Grid>
                        <IconButtonClose
                            color="inherit"
                            size="small"
                            onClick={() => onClose(null)}
                        />
                    </Grid>
                </Grid>
            </DialogTitle>

            <MprcRequestIndicator type="request" visible={formCycle == RequestLifeCycle.Loading} />
            <MprcRequestIndicator type="post" visible={formCycle == RequestLifeCycle.Posting} />

            {
                lastError && formCycle == RequestLifeCycle.LoadFailed
                    ? <MprcRequestErrorReport error={lastError} />
                    : (
                        <>
                            <MprcRequestErrorDialog error={lastError} onClose={() => setLastError(null)} />
                            {steps[step]()}
                        </>
                    )
            }

        </Dialog>
    );
};
